<template>
  <section class="px-3 pb-3">
    <AdminToastStack :toasts="toasts" @close="removeToast" />

    <div class="admin-surface">
      <div class="admin-surface__head">
        <span class="admin-surface__title">AI-профили</span>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-primary btn-sm" :disabled="isLoading" @click="loadProfiles">
            <i class="bi bi-arrow-repeat me-1"></i>
            Обновить
          </button>
          <button type="button" class="btn btn-primary btn-sm" :disabled="isLoading" @click="openCreateForm">
            <i class="bi bi-plus-lg me-1"></i>
            Создать
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2 px-3 mt-3 mb-0">{{ errorMessage }}</div>
      <div v-if="successMessage" class="alert alert-success py-2 px-3 mt-3 mb-0">{{ successMessage }}</div>

      <div class="row g-3 mt-1">
        <div class="col-12">
          <div class="table-responsive admin-table-wrap">
            <table class="table table-sm align-middle mb-0 admin-table">
              <thead>
                <tr>
                  <th>Ключ профиля</th>
                  <th>Активен</th>
                  <th class="text-end">Действия</th>
                </tr>
              </thead>
              <tbody v-if="profiles.length">
                <tr v-for="profile in profiles" :key="profile.id" :class="{ 'admin-table__row--active': profile.id === form.id }">
                  <td>{{ profile.profileKey || '—' }}</td>
                  <td>
                    <span :class="profile.isActive ? 'text-success' : 'text-danger'">
                      {{ profile.isActive ? 'Да' : 'Нет' }}
                    </span>
                  </td>
                  <td class="text-end">
                    <div class="btn-group btn-group-sm">
                      <button class="btn btn-outline-secondary" @click="openView(profile.id)">Просмотр</button>
                      <button class="btn btn-outline-primary" @click="openEdit(profile.id)">Изменить</button>
                      <button class="btn btn-outline-danger" @click="removeProfile(profile)">Удалить</button>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="3" class="text-center text-muted py-3">Профили не найдены.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="col-12">
          <div class="admin-detail">
            <div class="admin-detail__header">
              <div>
                <div class="admin-detail__title">{{ formTitle }}</div>
                <small class="text-muted">{{ formSubtitle }}</small>
              </div>
              <div class="d-flex gap-2">
                <button
                  v-if="mode === 'view' && form.id"
                  type="button"
                  class="btn btn-outline-primary btn-sm"
                  :disabled="isLoading"
                  @click="mode = 'edit'"
                >
                  Изменить
                </button>
                <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoading" @click="resetForm">
                  Очистить
                </button>
              </div>
            </div>

            <form class="mt-2" @submit.prevent="submitForm">
              <div class="row g-2">
                <div class="col-12 col-lg-6">
                  <label class="form-label mb-1">Ключ профиля *</label>
                  <input v-model.trim="form.profileKey" :disabled="isReadOnly" type="text" class="form-control form-control-sm" />
                </div>
                <div class="col-12 col-lg-6">
                  <label class="form-label mb-1">Версия</label>
                  <input v-model.number="form.version" :disabled="isReadOnly" min="1" type="number" class="form-control form-control-sm" />
                </div>
                <div class="col-12">
                  <label class="form-label mb-1">Описание</label>
                  <textarea v-model="form.description" :disabled="isReadOnly" rows="2" class="form-control form-control-sm"></textarea>
                </div>
                <div class="col-12 col-lg-6">
                  <label class="form-label mb-1">
                    Провайдер по умолчанию
                    <span v-if="defaultProviderRequired" class="text-danger">*</span>
                  </label>
                  <select
                    v-model="form.defaultProvider"
                    :disabled="isReadOnly || form.requireProviderModelFromContext"
                    class="form-select form-select-sm"
                  >
                    <option value="">—</option>
                    <option v-for="provider in AI_PROVIDER_VALUES" :key="provider" :value="provider">{{ provider }}</option>
                  </select>
                </div>
                <div class="col-12 col-lg-6">
                  <label class="form-label mb-1">
                    Модель по умолчанию
                    <span v-if="defaultProviderRequired" class="text-danger">*</span>
                  </label>
                  <input
                    v-model.trim="form.defaultModel"
                    :disabled="isReadOnly || form.requireProviderModelFromContext"
                    type="text"
                    class="form-control form-control-sm"
                  />
                </div>
                <div class="col-12">
                  <label class="form-label mb-1">Промпт по умолчанию</label>
                  <textarea
                    v-model="form.defaultPrompt"
                    :disabled="isReadOnly || !form.allowDefaultPrompt"
                    rows="3"
                    class="form-control form-control-sm"
                  ></textarea>
                </div>
                <div class="col-12 col-md-6">
                  <div class="form-check mt-1">
                    <input id="profile-is-active" v-model="form.isActive" :disabled="isReadOnly" class="form-check-input" type="checkbox" />
                    <label class="form-check-label" for="profile-is-active">Активный профиль</label>
                  </div>
                </div>
                <div class="col-12 col-md-6">
                  <div class="form-check mt-1">
                    <input
                      id="profile-schema-depends"
                      v-model="form.schemaDependsOnStreamType"
                      :disabled="isReadOnly"
                      class="form-check-input"
                      type="checkbox"
                    />
                    <label class="form-check-label" for="profile-schema-depends">Схема зависит от типа стрима</label>
                  </div>
                </div>
                <div class="col-12 col-md-6">
                  <div class="form-check mt-1">
                    <input
                      id="profile-require-context"
                      v-model="form.requireProviderModelFromContext"
                      :disabled="isReadOnly"
                      class="form-check-input"
                      type="checkbox"
                    />
                    <label class="form-check-label" for="profile-require-context">Провайдер/модель из контекста</label>
                  </div>
                </div>
                <div class="col-12 col-md-6">
                  <div class="form-check mt-1">
                    <input
                      id="profile-allow-prompt"
                      v-model="form.allowDefaultPrompt"
                      :disabled="isReadOnly"
                      class="form-check-input"
                      type="checkbox"
                    />
                    <label class="form-check-label" for="profile-allow-prompt">Разрешить промпт по умолчанию</label>
                  </div>
                </div>
              </div>

              <div class="mt-2">
                <AdminJsonTextarea
                  v-model="configText"
                  :disabled="isReadOnly"
                  label="Config JSON"
                  help-text="Пустое значение при создании будет отправлено как {}."
                  @validity-change="configValidation = $event"
                />
              </div>

              <div class="d-flex justify-content-end gap-2 mt-3">
                <button v-if="!isReadOnly" type="submit" class="btn btn-primary btn-sm" :disabled="isLoading">
                  {{ mode === 'create' ? 'Создать профиль' : 'Сохранить изменения' }}
                </button>
                <button
                  v-if="mode === 'edit' && form.id"
                  type="button"
                  class="btn btn-outline-danger btn-sm"
                  :disabled="isLoading"
                  @click="removeProfile(form)"
                >
                  Удалить
                </button>
              </div>
            </form>
          </div>

          <div class="admin-detail mt-3">
            <div class="admin-detail__header">
              <div>
                <div class="admin-detail__title">Схемы</div>
                <small class="text-muted">Управление в рамках выбранного AI-профиля</small>
              </div>
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                :disabled="isLoading || !canManageSchemas"
                @click="loadSchemasForCurrentProfile"
              >
                Обновить
              </button>
            </div>

            <div v-if="!canManageSchemas" class="text-muted small mt-2">
              Сначала сохраните новый профиль или откройте существующий.
            </div>

            <template v-else>
              <div class="table-responsive admin-table-wrap mt-2">
                <table class="table table-sm align-middle mb-0 admin-table">
                  <thead>
                    <tr>
                      <th>Скоуп стрима</th>
                      <th>JSON_SCHEMA</th>
                      <th>SCHEMA</th>
                      <th class="text-end">Действия</th>
                    </tr>
                  </thead>
                  <tbody v-if="groupedProfileSchemas.length">
                    <tr v-for="scope in groupedProfileSchemas" :key="`schema-scope-${scope.streamType || 'default'}`">
                      <td>{{ scope.streamType || 'DEFAULT' }}</td>
                      <td class="schema-value-preview">{{ scope.jsonSchema?.value || '—' }}</td>
                      <td class="schema-value-preview">{{ scope.schema?.value || '—' }}</td>
                      <td class="text-end">
                        <div class="btn-group btn-group-sm">
                          <button class="btn btn-outline-primary" @click="prefillSchemaScope(scope)">Изменить</button>
                          <button class="btn btn-outline-danger" @click="deleteSchemaScope(scope)">Удалить схемы</button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="4" class="text-center text-muted py-3">Для этого профиля схемы не найдены.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <form class="mt-2" @submit.prevent="submitSchemaForm">
                <div class="row g-2">
                  <div class="col-12">
                    <label class="form-label mb-1">Тип стрима</label>
                    <select v-model="schemaForm.streamType" :disabled="!canUseStreamScopes" class="form-select form-select-sm">
                      <option value="">DEFAULT (null)</option>
                      <option v-for="type in STREAM_TYPE_VALUES" :key="type" :value="type">{{ type }}</option>
                    </select>
                  </div>
                </div>

                <div class="mt-2">
                  <AdminJsonTextarea
                    v-model="schemaForm.jsonSchemaValue"
                    :allow-any-json="true"
                    label="JSON_SCHEMA"
                    help-text="Опционально. Если заполнено, должен быть валидный JSON."
                    @validity-change="jsonSchemaValidation = $event"
                  />
                </div>

                <div class="mt-2">
                  <AdminJsonTextarea
                    v-model="schemaForm.schemaValue"
                    :allow-any-json="true"
                    label="SCHEMA"
                    help-text="Опционально. Если заполнено, должен быть валидный JSON."
                    @validity-change="schemaValidation = $event"
                  />
                </div>

                <div class="d-flex justify-content-end gap-2 mt-3">
                  <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isLoading" @click="resetSchemaForm">
                    Очистить
                  </button>
                  <button type="submit" class="btn btn-primary btn-sm" :disabled="isLoading">Сохранить скоуп</button>
                </div>
              </form>
            </template>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import AdminToastStack from '@/admin/components/AdminToastStack.vue'
import AdminJsonTextarea from '@/admin/components/AdminJsonTextarea.vue'
import { useAdminRequestState } from '@/admin/composables/useAdminRequestState'
import {
  AI_PROVIDER_VALUES,
  STREAM_TYPE_VALUES,
  validateAdminAiProfileUpsertRequest,
  validateAdminSchemaUpsertRequest,
} from '@/admin/types/adminContracts'
import {
  createAdminAiProfile,
  deleteAdminAiProfile,
  deleteAdminSchema,
  getAdminAiProfileById,
  getAdminAiProfiles,
  getAdminSchemas,
  upsertAdminSchema,
  updateAdminAiProfile,
} from '@/admin/api/adminService'
import { parseJsonInput, stringifyJsonInput } from '@/admin/utils/adminApiUtils'

const DEFAULT_PROFILE_CONFIG = Object.freeze({
  reasoning: 'none',
  maxToolCalls: 1,
  responseMode: 'json_schema',
  enableJsonFix: true,
  useWebSearchTool: true,
})

const profiles = ref([])

const mode = ref('create')
const form = reactive({
  id: null,
  profileKey: '',
  description: '',
  isActive: true,
  schemaDependsOnStreamType: false,
  requireProviderModelFromContext: false,
  allowDefaultPrompt: true,
  defaultProvider: AI_PROVIDER_VALUES[0],
  defaultModel: '',
  config: null,
  defaultPrompt: '',
  version: 1,
})

const configText = ref('')
const configValidation = ref({ isValid: true, error: '' })

const profileSchemas = ref([])
const schemaForm = reactive({
  streamType: '',
  jsonSchemaValue: '',
  schemaValue: '',
})
const jsonSchemaValidation = ref({ isValid: true, error: '' })
const schemaValidation = ref({ isValid: true, error: '' })

const {
  isLoading,
  errorMessage,
  successMessage,
  toasts,
  removeToast,
  clearMessages,
  pushError,
  execute,
} = useAdminRequestState()

const defaultProviderRequired = computed(() => !form.requireProviderModelFromContext)
const isReadOnly = computed(() => mode.value === 'view')
const canManageSchemas = computed(() => Boolean(form.id && form.profileKey))
const canUseStreamScopes = computed(() => Boolean(form.schemaDependsOnStreamType))

const formTitle = computed(() => {
  if (mode.value === 'create') return 'Создание профиля'
  if (mode.value === 'edit') return `Редактирование #${form.id || ''}`
  return `Просмотр #${form.id || ''}`
})

const formSubtitle = computed(() => {
  if (mode.value === 'create') return 'Заполните обязательные поля и сохраните.'
  if (mode.value === 'edit') return 'На обновлении API изменяет только переданные непустые (не null) поля.'
  return 'Режим только для чтения.'
})

const normalizeSchemaSortKey = item => `${item.streamType || ''}|${item.param || ''}`

const groupedProfileSchemas = computed(() => {
  const byScope = new Map()
  profileSchemas.value.forEach(item => {
    const scopeKey = String(item.streamType || '')
    if (!byScope.has(scopeKey)) {
      byScope.set(scopeKey, {
        streamType: item.streamType || '',
        jsonSchema: null,
        schema: null,
      })
    }

    const scope = byScope.get(scopeKey)
    if (item.param === 'JSON_SCHEMA') scope.jsonSchema = item
    if (item.param === 'SCHEMA') scope.schema = item
  })

  return [...byScope.values()].sort((a, b) => String(a.streamType || '').localeCompare(String(b.streamType || '')))
})

const setFormFromProfile = profile => {
  form.id = profile?.id || null
  form.profileKey = profile?.profileKey || ''
  form.description = profile?.description || ''
  form.isActive = Boolean(profile?.isActive)
  form.schemaDependsOnStreamType = Boolean(profile?.schemaDependsOnStreamType)
  form.requireProviderModelFromContext = Boolean(profile?.requireProviderModelFromContext)
  form.allowDefaultPrompt = profile?.allowDefaultPrompt == null ? true : Boolean(profile.allowDefaultPrompt)
  form.defaultProvider = profile?.defaultProvider || ''
  form.defaultModel = profile?.defaultModel || ''
  form.defaultPrompt = profile?.defaultPrompt || ''
  form.version = Number(profile?.version || 1)
  configText.value = stringifyJsonInput(profile?.config)
}

const resetSchemaForm = () => {
  schemaForm.streamType = ''
  schemaForm.jsonSchemaValue = ''
  schemaForm.schemaValue = ''
}

const resetForm = () => {
  mode.value = 'create'
  setFormFromProfile(null)
  form.isActive = true
  form.schemaDependsOnStreamType = false
  form.requireProviderModelFromContext = false
  form.allowDefaultPrompt = true
  form.defaultProvider = AI_PROVIDER_VALUES[0]
  form.defaultModel = ''
  form.defaultPrompt = ''
  form.version = 1
  configText.value = stringifyJsonInput(DEFAULT_PROFILE_CONFIG)
  profileSchemas.value = []
  resetSchemaForm()
}

const loadProfiles = async () => {
  clearMessages()
  const list = await execute(() => getAdminAiProfiles({ includeInactive: true }), {
    error: 'Не удалось загрузить AI-профили.',
  })
  profiles.value = list
}

const loadSchemasForCurrentProfile = async () => {
  if (!form.profileKey) {
    profileSchemas.value = []
    return
  }
  const list = await execute(() => getAdminSchemas({ profileKey: form.profileKey }), {
    error: 'Не удалось загрузить схемы профиля.',
  })
  profileSchemas.value = [...list].sort((a, b) => normalizeSchemaSortKey(a).localeCompare(normalizeSchemaSortKey(b)))
}

const openCreateForm = () => {
  clearMessages()
  resetForm()
}

const openView = async profileId => {
  clearMessages()
  const profile = await execute(() => getAdminAiProfileById(profileId), {
    error: 'Не удалось загрузить AI-профиль.',
  })
  mode.value = 'view'
  setFormFromProfile(profile)
  resetSchemaForm()
  await loadSchemasForCurrentProfile()
}

const openEdit = async profileId => {
  clearMessages()
  const profile = await execute(() => getAdminAiProfileById(profileId), {
    error: 'Не удалось загрузить AI-профиль.',
  })
  mode.value = 'edit'
  setFormFromProfile(profile)
  resetSchemaForm()
  await loadSchemasForCurrentProfile()
}

const buildProfilePayload = () => {
  const configResult = parseJsonInput(configText.value, { allowEmpty: true })
  if (!configResult.isValid) return { payload: null, error: configResult.error }

  const configValue = mode.value === 'create' ? configResult.value || { ...DEFAULT_PROFILE_CONFIG } : configResult.value

  return {
    payload: {
      profileKey: form.profileKey,
      description: form.description,
      isActive: form.isActive,
      schemaDependsOnStreamType: form.schemaDependsOnStreamType,
      requireProviderModelFromContext: form.requireProviderModelFromContext,
      allowDefaultPrompt: form.allowDefaultPrompt,
      defaultProvider: form.defaultProvider || null,
      defaultModel: form.defaultModel || null,
      config: configValue,
      defaultPrompt: form.allowDefaultPrompt ? form.defaultPrompt || null : null,
      version: Number(form.version || 1),
    },
    error: '',
  }
}

const submitForm = async () => {
  if (isReadOnly.value) return
  clearMessages()

  const validationErrors = validateAdminAiProfileUpsertRequest(form)
  if (validationErrors.length) {
    pushError(validationErrors[0])
    return
  }
  if (mode.value === 'create') {
    const normalizedProfileKey = String(form.profileKey || '').trim()
    const alreadyExists = profiles.value.some(item => item.profileKey === normalizedProfileKey)
    if (alreadyExists) {
      pushError('Ключ профиля должен быть уникальным.')
      return
    }
  }
  if (!configValidation.value.isValid) {
    pushError(configValidation.value.error || 'Невалидный JSON в config.')
    return
  }

  const built = buildProfilePayload()
  if (built.error) {
    pushError(built.error)
    return
  }

  if (mode.value === 'create') {
    const created = await execute(() => createAdminAiProfile(built.payload), {
      success: 'AI-профиль создан.',
      error: 'Не удалось создать AI-профиль.',
    })
    mode.value = 'edit'
    setFormFromProfile(created)
  } else {
    const updated = await execute(() => updateAdminAiProfile(form.id, built.payload), {
      success: 'AI-профиль обновлен.',
      error: 'Не удалось обновить AI-профиль.',
    })
    mode.value = 'edit'
    setFormFromProfile(updated)
  }

  await loadProfiles()
  await loadSchemasForCurrentProfile()
}

const removeProfile = async profile => {
  const id = Number(profile?.id || 0)
  if (!id) return
  const confirmed = window.confirm(`Удалить AI-профиль #${id}?`)
  if (!confirmed) return

  clearMessages()
  await execute(() => deleteAdminAiProfile(id), {
    success: 'AI-профиль удален.',
    error: 'Не удалось удалить AI-профиль.',
  })

  if (form.id === id) resetForm()
  await loadProfiles()
}

const prefillSchemaScope = scope => {
  schemaForm.streamType = scope.streamType || ''
  schemaForm.jsonSchemaValue = stringifyJsonInput(scope.jsonSchema?.value)
  schemaForm.schemaValue = stringifyJsonInput(scope.schema?.value)
}

const submitSchemaForm = async () => {
  if (!canManageSchemas.value) {
    pushError('Сначала сохраните профиль или откройте существующий.')
    return
  }
  clearMessages()

  const jsonSchemaText = String(schemaForm.jsonSchemaValue || '').trim()
  const schemaText = String(schemaForm.schemaValue || '').trim()
  if (!jsonSchemaText && !schemaText) {
    pushError('Заполните как минимум одно поле: JSON_SCHEMA или SCHEMA.')
    return
  }

  if (!jsonSchemaValidation.value.isValid) {
    pushError(jsonSchemaValidation.value.error || 'Невалидный JSON в JSON_SCHEMA.')
    return
  }
  if (!schemaValidation.value.isValid) {
    pushError(schemaValidation.value.error || 'Невалидный JSON в SCHEMA.')
    return
  }

  const streamType = canUseStreamScopes.value ? schemaForm.streamType || null : null
  const requests = []

  if (jsonSchemaText) {
    const jsonSchemaPayload = {
      profileKey: form.profileKey,
      param: 'JSON_SCHEMA',
      streamType,
      value: schemaForm.jsonSchemaValue,
    }
    const validationErrors = validateAdminSchemaUpsertRequest(jsonSchemaPayload)
    if (validationErrors.length) {
      pushError(validationErrors[0])
      return
    }
    const parsed = parseJsonInput(schemaForm.jsonSchemaValue, { allowEmpty: false, allowAnyJson: true })
    if (!parsed.isValid) {
      pushError(parsed.error)
      return
    }
    requests.push(jsonSchemaPayload)
  }

  if (schemaText) {
    const schemaPayload = {
      profileKey: form.profileKey,
      param: 'SCHEMA',
      streamType,
      value: schemaForm.schemaValue,
    }
    const validationErrors = validateAdminSchemaUpsertRequest(schemaPayload)
    if (validationErrors.length) {
      pushError(validationErrors[0])
      return
    }
    const parsed = parseJsonInput(schemaForm.schemaValue, { allowEmpty: false, allowAnyJson: true })
    if (!parsed.isValid) {
      pushError(parsed.error)
      return
    }
    requests.push(schemaPayload)
  }

  await execute(
    async () => {
      for (const request of requests) {
        await upsertAdminSchema(request)
      }
    },
    {
      success: 'Схемы скоупа сохранены.',
      error: 'Не удалось сохранить схемы скоупа.',
    }
  )

  resetSchemaForm()
  await loadSchemasForCurrentProfile()
}

const deleteSchemaParamSafely = async (scope, param) => {
  try {
    await deleteAdminSchema({
      profileKey: form.profileKey,
      param,
      streamType: scope.streamType,
    })
    return true
  } catch (error) {
    const status = Number(error?.response?.status || 0)
    if (status === 404) return false
    throw error
  }
}

const deleteSchemaScope = async scope => {
  const streamLabel = scope.streamType || 'DEFAULT'
  const confirmed = window.confirm(`Удалить схемы для скоупа стрима ${streamLabel}?`)
  if (!confirmed) return

  clearMessages()
  await execute(
    async () => {
      await deleteSchemaParamSafely(scope, 'JSON_SCHEMA')
      await deleteSchemaParamSafely(scope, 'SCHEMA')
    },
    {
      success: 'Схемы скоупа удалены.',
      error: 'Не удалось удалить схемы скоупа.',
    }
  )

  await loadSchemasForCurrentProfile()
}

watch(
  () => form.requireProviderModelFromContext,
  enabled => {
    if (!enabled && !form.defaultProvider) {
      form.defaultProvider = AI_PROVIDER_VALUES[0]
    }
  }
)

watch(
  () => form.schemaDependsOnStreamType,
  enabled => {
    if (!enabled) schemaForm.streamType = ''
  }
)

onMounted(async () => {
  resetForm()
  await loadProfiles()
})
</script>

<style scoped>
.admin-surface {
  border-radius: 14px;
  border: 1px solid #dce7fa;
  background: #fff;
  padding: 12px;
}

.admin-surface__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.admin-surface__title {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #d8e5fb;
  background: #eff5ff;
  color: #3b5690;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 5px 9px;
}

.admin-table-wrap {
  border: 1px solid #e7edf8;
  border-radius: 12px;
  max-height: 72vh;
  overflow: auto;
}

.admin-table thead th {
  position: sticky;
  top: 0;
  background: #f8fbff;
  z-index: 2;
  color: #607089;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #e9edf6;
}

.admin-table__row--active td {
  background: #eef5ff;
}

.admin-detail {
  border: 1px solid #e4ebf8;
  border-radius: 12px;
  background: #fbfdff;
  padding: 10px;
}

.admin-detail__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.admin-detail__title {
  color: #1f2d45;
  font-weight: 700;
}

.schema-value-preview {
  max-width: 260px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
